import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { todayIso } from "../../lib/finance";
import { SIGNATURE_MODES, recordsError } from "../../lib/records";
import { RecordFilesField } from "./RecordBits";
import { ClassificationField, ReadersPicker } from "./RecordFields";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

function useRequest() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const run = async (request, onDone, success) => {
    setBusy(true);
    setError("");
    try {
      const { data } = await request();
      if (success) toast.success(success);
      onDone(data);
    } catch (e) {
      setError(recordsError(e, "Enregistrement impossible. Réessayez."));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, setError, run };
}

function Footer({ req, onCancel, onSubmit, disabled, label, danger, testid }) {
  return (
    <>
      {req.error && <p className="text-sm text-red-600" role="alert" data-testid="records-dialog-error">{req.error}</p>}
      <DialogFooter>
        <Button variant="outline" onClick={onCancel} disabled={req.busy}>Fermer</Button>
        <Button variant={danger ? "destructive" : "default"} onClick={onSubmit} disabled={req.busy || disabled} data-testid={testid}>
          {req.busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{label}
        </Button>
      </DialogFooter>
    </>
  );
}

function Row({ id, label, hint, children }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {hint && <p className="text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

const post = (doc, action, body) => api.post(`/records/documents/${doc.id}/${action}`, { expected_version: doc.version, ...body });

// Submit and approve: a plain confirmation (the server decides who may approve — never the author).
export function ConfirmDialog({ doc, action, open, onClose, onDone }) {
  const req = useRequest();
  const text = {
    submit: { title: "Soumettre en revue ?", body: "Une autre personne autorisée vérifiera la version en cours avant approbation.", label: "Soumettre", success: "Document soumis en revue." },
    approve: { title: "Approuver ce document ?", body: "La version en cours est approuvée : il reste à consigner la signature avec la copie signée.", label: "Approuver", success: "Document approuvé." },
  }[action];
  if (!text) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>{text.title}</DialogTitle><DialogDescription>{text.body}</DialogDescription></DialogHeader>
        <Footer req={req} onCancel={onClose} label={text.label} testid={`records-confirm-${action}`}
          onSubmit={() => req.run(() => post(doc, action, {}), onDone, text.success)} />
      </DialogContent>
    </Dialog>
  );
}

export function VersionDialog({ doc, open, onClose, onDone }) {
  const req = useRequest();
  const [files, setFiles] = useState([]);
  const [note, setNote] = useState("");
  useEffect(() => { if (open) { setFiles([]); setNote(""); req.setError(""); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{doc.version_count ? "Nouvelle version" : "Ajouter le fichier"}</DialogTitle>
          <DialogDescription>Les versions précédentes restent consultables.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <RecordFilesField files={files} setFiles={setFiles} required label="Fichier de la version" testid="records-version-files" />
          <Row id="records-version-note" label="Note">
            <Input id="records-version-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} className="h-10" placeholder="Ex. corrections du fournisseur" />
          </Row>
        </div>
        <Footer req={req} onCancel={onClose} label="Ajouter la version" disabled={!files.length} testid="records-version-submit"
          onSubmit={() => req.run(() => api.post(`/records/documents/${doc.id}/versions`, { expected_version: doc.version, file_ids: files.map((f) => f.id), note: note.trim() }),
            onDone, "Version ajoutée.")} />
      </DialogContent>
    </Dialog>
  );
}

function NamesInput({ id, value, onChange, testid }) {
  return <Input id={id} value={value} onChange={(e) => onChange(e.target.value)} className="h-10" placeholder="Noms séparés par des virgules" data-testid={testid} />;
}
const names = (text) => text.split(",").map((s) => s.trim()).filter(Boolean);

export function SignDialog({ doc, open, onClose, onDone }) {
  const req = useRequest();
  const [files, setFiles] = useState([]);
  const [form, setForm] = useState({ signed_on: todayIso(), mode: "handwritten", jamora: "", counterpart: "", note: "" });
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  useEffect(() => { if (open) { setFiles([]); setForm({ signed_on: todayIso(), mode: "handwritten", jamora: "", counterpart: "", note: "" }); req.setError(""); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Consigner la signature</DialogTitle>
          <DialogDescription>La copie signée devient la version de référence, figée. Ensuite, toute modification passe par un avenant.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <RecordFilesField files={files} setFiles={setFiles} required label="Copie signée" testid="records-sign-files" />
          <div className="grid gap-4 sm:grid-cols-2">
            <Row id="records-signed-on" label="Date de signature">
              <Input id="records-signed-on" type="date" max={todayIso()} value={form.signed_on} onChange={(e) => set("signed_on")(e.target.value)} className="h-10" data-testid="records-sign-date" />
            </Row>
            <Row id="records-sign-mode" label="Signature">
              <Select value={form.mode} onValueChange={set("mode")}>
                <SelectTrigger id="records-sign-mode" className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(SIGNATURE_MODES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </Row>
          </div>
          <Row id="records-sign-jamora" label="Signataires pour Jamora"><NamesInput id="records-sign-jamora" value={form.jamora} onChange={set("jamora")} testid="records-sign-jamora" /></Row>
          <Row id="records-sign-counterpart" label="Signataires des autres parties"><NamesInput id="records-sign-counterpart" value={form.counterpart} onChange={set("counterpart")} /></Row>
          <Row id="records-sign-note" label="Note"><Input id="records-sign-note" value={form.note} onChange={(e) => set("note")(e.target.value)} maxLength={1000} className="h-10" /></Row>
        </div>
        <Footer req={req} onCancel={onClose} label="Consigner la signature" disabled={!files.length || !form.signed_on || !(names(form.jamora).length || names(form.counterpart).length)} testid="records-sign-submit"
          onSubmit={() => req.run(() => post(doc, "sign", {
            file_ids: files.map((f) => f.id), signed_on: form.signed_on, mode: form.mode, jamora_signatories: names(form.jamora),
            counterpart_signatories: names(form.counterpart), note: form.note.trim(),
          }), onDone, "Signature consignée.")} />
      </DialogContent>
    </Dialog>
  );
}

const REASON_TEXT = {
  return: { title: "Renvoyer en brouillon", body: "L'auteur reprend le document. Dites-lui ce qu'il faut changer.", label: "Renvoyer", success: "Document renvoyé en brouillon." },
  archive: { title: "Archiver le document", body: "Il sort du registre en cours mais reste consultable avec ses fichiers.", label: "Archiver", success: "Document archivé.", danger: true },
  abandon: { title: "Abandonner le document", body: "Le document n'ira pas plus loin. Il reste consultable.", label: "Abandonner", success: "Document abandonné.", danger: true },
};

export function ReasonDialog({ doc, action, open, onClose, onDone }) {
  const req = useRequest();
  const [reason, setReason] = useState("");
  useEffect(() => { if (open) { setReason(""); req.setError(""); } }, [open, action]); // eslint-disable-line react-hooks/exhaustive-deps
  const text = REASON_TEXT[action];
  if (!text) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>{text.title}</DialogTitle><DialogDescription>{text.body}</DialogDescription></DialogHeader>
        <Row id="records-reason" label="Motif">
          <Textarea id="records-reason" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} rows={3} data-testid="records-reason" />
        </Row>
        <Footer req={req} onCancel={onClose} label={text.label} danger={text.danger} disabled={reason.trim().length < 3} testid={`records-reason-${action}`}
          onSubmit={() => req.run(() => post(doc, action, { reason: reason.trim() }), onDone, text.success)} />
      </DialogContent>
    </Dialog>
  );
}

export function TerminateDialog({ doc, open, onClose, onDone }) {
  const req = useRequest();
  const [day, setDay] = useState(todayIso());
  const [reason, setReason] = useState("");
  useEffect(() => { if (open) { setDay(todayIso()); setReason(""); req.setError(""); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Résilier « {doc.title} »</DialogTitle>
          <DialogDescription>Le CRM consigne la résiliation : la lettre elle-même s'envoie en dehors. Le document reste consultable.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Row id="records-terminated-on" label="Date de fin">
            <Input id="records-terminated-on" type="date" value={day} onChange={(e) => setDay(e.target.value)} className="h-10" data-testid="records-terminate-date" />
          </Row>
          <Row id="records-terminate-reason" label="Motif">
            <Textarea id="records-terminate-reason" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} rows={3} data-testid="records-terminate-reason" />
          </Row>
        </div>
        <Footer req={req} onCancel={onClose} label="Résilier" danger disabled={!day || reason.trim().length < 3} testid="records-terminate-submit"
          onSubmit={() => req.run(() => post(doc, "terminate", { terminated_on: day, reason: reason.trim() }), onDone, "Résiliation consignée.")} />
      </DialogContent>
    </Dialog>
  );
}

export function AccessDialog({ doc, people, canConfidential, open, onClose, onDone }) {
  const req = useRequest();
  const [classification, setClassification] = useState(doc.classification);
  const [readers, setReaders] = useState(doc.reader_ids || []);
  useEffect(() => { if (open) { setClassification(doc.classification); setReaders(doc.reader_ids || []); req.setError(""); } }, [open, doc]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader><DialogTitle>Gérer l'accès</DialogTitle><DialogDescription>Qui voit ce document et ses fichiers.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <ClassificationField value={classification} onChange={setClassification} canConfidential={canConfidential} />
          {classification === "restricted" && <ReadersPicker people={people} value={readers} onChange={setReaders} classification={classification} />}
        </div>
        <Footer req={req} onCancel={onClose} label="Enregistrer" testid="records-access-submit"
          onSubmit={() => req.run(() => api.put(`/records/documents/${doc.id}/access`, {
            expected_version: doc.version, classification, reader_ids: classification === "restricted" ? readers : [],
          }), onDone, "Accès mis à jour.")} />
      </DialogContent>
    </Dialog>
  );
}

// Content (title, description, dates) only while a draft; the responsible and the retention at any time (the server enforces it).
export function EditDialog({ doc, people, open, onClose, onDone }) {
  const req = useRequest();
  const content = !!doc.capabilities?.edit_content;
  const initial = () => ({
    title: doc.title, description: doc.description || "", effective_date: doc.effective_date || "", expiry_date: doc.expiry_date || "",
    notice_days: doc.notice_days ?? "", tacit_renewal: !!doc.tacit_renewal, responsible_user_id: doc.responsible_user_id || "",
    retention_until: doc.retention_until || "",
  });
  const [form, setForm] = useState(initial);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  useEffect(() => { if (open) { setForm(initial()); req.setError(""); } }, [open, doc]); // eslint-disable-line react-hooks/exhaustive-deps
  const responsibles = people.filter((p) => doc.classification !== "confidential" || p.confidential);
  const body = () => {
    const out = { expected_version: doc.version };
    if (content) {
      Object.assign(out, { title: form.title.trim(), description: form.description.trim(), tacit_renewal: form.tacit_renewal,
        notice_days: form.notice_days === "" ? null : Number(form.notice_days) });
      if (form.effective_date) out.effective_date = form.effective_date;
      if (form.expiry_date) out.expiry_date = form.expiry_date;
    }
    if (form.responsible_user_id && form.responsible_user_id !== doc.responsible_user_id) out.responsible_user_id = form.responsible_user_id;
    if (form.retention_until && form.retention_until !== doc.retention_until) out.retention_until = form.retention_until;
    return out;
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Modifier le document</DialogTitle>
          <DialogDescription>{content ? "Brouillon : tout se modifie." : "Le contenu est figé : seuls le responsable et la conservation se modifient. Pour le reste, créez un avenant."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {content && (
            <>
              <Row id="records-edit-title" label="Titre"><Input id="records-edit-title" value={form.title} onChange={(e) => set("title")(e.target.value)} maxLength={200} className="h-10" data-testid="records-edit-title" /></Row>
              <Row id="records-edit-description" label="Description"><Textarea id="records-edit-description" value={form.description} onChange={(e) => set("description")(e.target.value)} maxLength={4000} rows={3} /></Row>
              <div className="grid gap-4 sm:grid-cols-3">
                <Row id="records-edit-effective" label="Date d'effet"><Input id="records-edit-effective" type="date" value={form.effective_date} onChange={(e) => set("effective_date")(e.target.value)} className="h-10" /></Row>
                <Row id="records-edit-expiry" label="Date d'expiration"><Input id="records-edit-expiry" type="date" value={form.expiry_date} onChange={(e) => set("expiry_date")(e.target.value)} className="h-10" /></Row>
                <Row id="records-edit-notice" label="Préavis (jours)"><Input id="records-edit-notice" type="number" min={0} max={730} value={form.notice_days} onChange={(e) => set("notice_days")(e.target.value)} className="h-10" /></Row>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700"><Checkbox checked={form.tacit_renewal} onCheckedChange={(v) => set("tacit_renewal")(!!v)} />Reconduction tacite</label>
            </>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <Row id="records-edit-responsible" label="Responsable">
              <Select value={form.responsible_user_id} onValueChange={set("responsible_user_id")}>
                <SelectTrigger id="records-edit-responsible" className="h-10"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                <SelectContent>
                  {!responsibles.some((p) => p.id === doc.responsible_user_id) && doc.responsible_user_id && (
                    <SelectItem value={doc.responsible_user_id}>{doc.responsible_name || "Responsable actuel"}</SelectItem>
                  )}
                  {responsibles.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Row>
            <Row id="records-edit-retention" label="Conserver jusqu'au" hint="Calculée depuis le type ; modifiable si une règle particulière s'applique.">
              <Input id="records-edit-retention" type="date" value={form.retention_until} onChange={(e) => set("retention_until")(e.target.value)} className="h-10" />
            </Row>
          </div>
        </div>
        <Footer req={req} onCancel={onClose} label="Enregistrer" testid="records-edit-submit"
          disabled={content && (form.title.trim().length < 2 || (form.notice_days !== "" && !form.expiry_date))}
          onSubmit={() => req.run(() => api.patch(`/records/documents/${doc.id}`, body()), onDone, "Modifications enregistrées.")} />
      </DialogContent>
    </Dialog>
  );
}
